import { useEffect, useRef, useState } from 'react';
import { AGENT_STARTED_MS, agentStartedAnnouncement } from './island-agent-started';
import { useIslandState } from './use-island-state';
import { useIslandQuickView } from './use-island-quickview';
import { useMyAgents } from './my-agents';
import { useProductName } from './use-brand';

/** The transient announcement: the name to say and the single started agent's id for the pill's leading
 *  glyph (`''` = the Maestro / agent-less bucket). Shared shape with native (`agentStartedName` +
 *  `agentStartedAgentId`). */
export interface IslandAgentStarted {
  name: string;
  agentId: string;
}

/**
 * The island's transient "{agentName} started working" announcement, or null. Watches the GLOBAL in-motion
 * count and announces when it RISES — the rule lives in the pure `agentStartedAnnouncement`; this hook only
 * supplies the baseline, the naming data and the 5s window.
 *
 * The baseline starts unset, so the FIRST count this browser sees seeds silently: whatever is already running
 * when the app loads never announces itself, and a remount (a route change) is likewise quiet rather than
 * replaying an old start.
 */
export function useIslandAgentStarted(): IslandAgentStarted | null {
  const island = useIslandState();
  const quickView = useIslandQuickView();
  const teamState = useMyAgents();
  const productName = useProductName();
  const [started, setStarted] = useState<IslandAgentStarted | null>(null);
  const seen = useRef<number | null>(null);
  // The 5s auto-clear timer is OWNED by a ref, not by the effect's cleanup — so a SILENT effect pass
  // (a dep changed but the count didn't RISE, e.g. the task finishes and `inMotionCount` falls) leaves a
  // live flash's own timer running instead of cancelling it.
  const clearTimer = useRef<number | null>(null);

  // The naming data is read through a ref rather than the effect's deps ON PURPOSE: both arrays are rebuilt
  // every render, so depending on them would re-run the effect constantly — and its cleanup would clear the
  // 5s timer, cutting the announcement short on the next render. The count is the only real trigger.
  const naming = useRef<{ tasks: Parameters<typeof agentStartedAnnouncement>[0]['tasks']; agents: { id?: string; name?: string }[] }>({
    tasks: [],
    agents: [],
  });
  naming.current = {
    tasks: quickView.status === 'ready' ? quickView.data.sorted : [],
    agents: teamState.status === 'ready' ? teamState.team.agents : [],
  };

  // Only observe once the COUNT's own source has landed, or the baseline seeds off a pre-data zero and the
  // first real count reads as a rise.
  const ready = quickView.status === 'ready';
  const inMotionCount = island.inMotionCount;

  useEffect(() => {
    if (!ready) return undefined;
    const { name: announced, agentId, count } = agentStartedAnnouncement({
      prevInMotionCount: seen.current,
      inMotionCount,
      tasks: naming.current.tasks,
      agents: naming.current.agents,
      // The Maestro is the fallback when the newest in-progress task resolves to no roster name.
      mainAgentName: productName,
    });
    seen.current = count;
    // A silent pass (no rise) must NOT touch a live flash's timer — return without clearing it, so an
    // announcement already on screen still auto-clears after its own window.
    if (!announced) return undefined;
    setStarted({ name: announced, agentId: agentId ?? '' });
    if (clearTimer.current !== null) window.clearTimeout(clearTimer.current);
    clearTimer.current = window.setTimeout(() => {
      setStarted(null);
      clearTimer.current = null;
    }, AGENT_STARTED_MS);
    return undefined;
  }, [ready, inMotionCount, productName]);

  // Cancel a pending clear on UNMOUNT only (never on a dep change) — so the timer can't fire into an
  // unmounted component, without a silent re-run being able to cut a live flash short.
  useEffect(
    () => () => {
      if (clearTimer.current !== null) window.clearTimeout(clearTimer.current);
    },
    [],
  );

  return started;
}
